import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { UUID } from "@bitory-gmbh/nidavellir";
import { SampleViewModel } from "@/services/backend/samples/types/sample-view-model";
import { useState } from "react";
import { CommonDialogFooter } from "@/shared/components/dialog/dialog-common";
import {
  PutRequest,
  useSamplesDeliveryPutMutation,
} from "@/services/backend/samples/api/delivery-api";
import { LaboratorySelect } from "@/services/backend/samples/components/laboratory-select";
import { v4 } from "uuid";
import { Field, FieldError, FieldLabel } from "@/shared/components/ui/field";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import {
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@/shared/components/ui/table";
import { AlertTriangle, X } from "lucide-react";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { toast } from "sonner";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import {
  ScrollBodyTable,
  ScrollBodyTableHeader,
} from "@/shared/components/tables/table-scroll-body";
import { fieldErrors } from "@/shared/components/errors/field-errors";
import { newItemFrom } from "@/services/backend/samples/types/delivery";

export function CreateDeliveryDialog({
  documentId,
  documentNamespace,
  selected,
  onSuccess,
}: {
  documentId: UUID;
  documentNamespace: string;
  selected: SampleViewModel[];
  onSuccess: () => void;
}) {
  const [open, setOpen] = useState(false);

  const [samples, setSamples] = useState(selected);

  const [request, setRequest] = useState<PutRequest>(
    initRequest(documentId, documentNamespace, selected),
  );

  const [put, { isLoading, error, reset }] = useSamplesDeliveryPutMutation();

  const resetState = () => {
    setRequest(initRequest(documentId, documentNamespace, selected));
    setSamples(selected);
    reset();
  };

  const doCreate = () => {
    if (isLoading) return;
    put({
      ...request,
      items: samples.map((s) => newItemFrom(s, request.id)),
    })
      .unwrap()
      .then(() => {
        toast.success("Lieferung erstellt");
        resetState();
        setOpen(false);
        onSuccess();
      });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        resetState();
        setOpen(value);
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm">{t("Neue Lieferung")}</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-6xl">
        <DialogHeader>
          <DialogTitle>{t("Neue Lieferung")}</DialogTitle>
          <DialogDescription>
            {t(
              "Hier können Sie ein neue Lieferung an ein Labor anlegen. Die ausgewählten Proben werden direkt mit diesem Lieferung verknüpft. Sie können im Anschluss weitere Proben hinzufügen / entfernen.",
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field>
            <FieldLabel>{t("Labor")}</FieldLabel>
            <LaboratorySelect
              labId={request.labId}
              onLabIdChange={(labId) => setRequest({ ...request, labId })}
            />
            <FieldError errors={fieldErrors(error, "labId")} />
          </Field>
          <Field>
            <FieldLabel>{t("Tracking ID")}</FieldLabel>
            <Input
              value={request.trackingId}
              onChange={(e) =>
                setRequest({ ...request, trackingId: e.target.value })
              }
            />
            <FieldError errors={fieldErrors(error, "trackingId")} />
          </Field>
          <Field className="col-span-2">
            <FieldLabel>{t("Beschreibung (Notiz an das Labor)")}</FieldLabel>
            <Textarea
              value={request.description}
              onChange={(e) =>
                setRequest({ ...request, description: e.target.value })
              }
            />
            <FieldError errors={fieldErrors(error, "description")} />
          </Field>
          <div className="col-span-2">
            <ScrollArea className={error ? "h-[20vh]" : "h-[40vh]"}>
              <ScrollBodyTable>
                <ScrollBodyTableHeader>
                  <TableRow>
                    <TableHead>{t("Nummer")}</TableHead>
                    <TableHead>{t("Art")}</TableHead>
                    <TableHead>{t("Ort")}</TableHead>
                    <TableHead>{t("Stelle")}</TableHead>
                    <TableHead />
                    <TableHead />
                  </TableRow>
                </ScrollBodyTableHeader>
                <TableBody>
                  {samples.map((sample) => (
                    <SampleTableRow
                      key={sample.id}
                      deliveryId={request.id}
                      labId={request.labId}
                      sample={sample}
                      onRemove={(s) =>
                        setSamples(samples.filter((v) => v.id !== s.id))
                      }
                    />
                  ))}
                </TableBody>
              </ScrollBodyTable>
            </ScrollArea>
          </div>
        </div>
        <RTKQueryErrorAlert error={error} showValidationErrors />
        <CommonDialogFooter
          actionLabel={t("Lieferung anlegen")}
          isLoading={false}
          onClickAction={doCreate}
        />
      </DialogContent>
    </Dialog>
  );
}

function initRequest(
  documentId: UUID,
  documentNamespace: string,
  samples: SampleViewModel[],
): PutRequest {
  return {
    id: v4(),
    documentId,
    documentNamespace,
    trackingId: "",
    description: "",
    labId:
      samples
        .filter((s) => s.labId !== null)
        .map((s) => s.labId)
        .pop() || null,
    items: [],
  };
}

function SampleTableRow({
  deliveryId,
  labId,
  sample,
  onRemove,
}: {
  deliveryId: UUID;
  labId: UUID | null;
  sample: SampleViewModel;
  onRemove: (s: SampleViewModel) => void;
}) {
  const hasDifferentLab =
    labId !== null && sample.labId !== null && sample.labId !== labId;
  const isInOtherDelivery =
    sample.deliveryIds !== null &&
    sample.deliveryIds.some((dId) => dId !== deliveryId);

  return (
    <TableRow>
      <TableCell>{sample.number}</TableCell>
      <TableCell>{t(sample.kind)}</TableCell>
      <TableCell>{sample.location}</TableCell>
      <TableCell>{sample.spot}</TableCell>
      <TableCell>
        {hasDifferentLab && (
          <span className="text-destructive flex items-center gap-2">
            <AlertTriangle className="size-5" />
            {t("Ist einem anderen Labor zugeordnet")}
          </span>
        )}
        {isInOtherDelivery && (
          <span className="text-destructive flex items-center gap-2">
            <AlertTriangle className="size-5" />
            {t("Ist schon einer anderen Lieferung zugeordnet")}
          </span>
        )}
      </TableCell>
      <TableCell className="text-right">
        <Button
          size="sm"
          variant="destructive"
          onClick={() => onRemove(sample)}
        >
          <X />
        </Button>
      </TableCell>
    </TableRow>
  );
}
